import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Autocomplete,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { ChatResource, ChatMessage } from "../../api/chat";
import { ApiError } from "../../api/apiFetch";
import { formatDateTime } from "./utils";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";

interface Sender {
  id: string;
  name: string;
}

interface MeetingChatDialogProps {
  open: boolean;
  meetingId: string | null;
  meetingTitle: string;
  onClose: () => void;
}

const PAGE_SIZE = 50;

export const MeetingChatDialog: React.FC<MeetingChatDialogProps> = ({
  open,
  meetingId,
  meetingTitle,
  onClose,
}) => {
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [allMessages, setAllMessages] = React.useState<ChatMessage[]>([]); // Store all messages for sender list
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // True when the meeting has no chat channel yet (e.g. never started).
  const [noChat, setNoChat] = React.useState(false);
  const [totalMessages, setTotalMessages] = React.useState<number>(0);
  const [deletingMessageId, setDeletingMessageId] = React.useState<
    string | null
  >(null);
  const [nextToken, setNextToken] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedSenders, setSelectedSenders] = React.useState<Sender[]>([]);
  // Timestamp order: "asc" = oldest first (default), "desc" = newest first.
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });
  const [deleteConfirmation, setDeleteConfirmation] = React.useState<{
    open: boolean;
    messageId: string | null;
    messagePreview: string;
    senderName: string;
  }>({ open: false, messageId: null, messagePreview: "", senderName: "" });

  const { withLoading } = useGlobalLoading();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // A missing chat channel isn't a real error — it just means the meeting has
  // no chat yet. The chat-api currently surfaces this as 500 "Channel not
  // found", so match on the message too (not only a 404 status).
  const isNoChatError = (err: unknown): boolean => {
    if (err instanceof ApiError && err.status === 404) return true;
    const message = err instanceof Error ? err.message : String(err);
    return /channel not found/i.test(message);
  };

  // Extract unique senders from all messages
  const availableSenders = React.useMemo(() => {
    const senderMap = new Map<string, Sender>();
    allMessages.forEach((msg) => {
      if (!senderMap.has(msg.senderId)) {
        senderMap.set(msg.senderId, {
          id: msg.senderId,
          name: msg.senderName,
        });
      }
    });
    return Array.from(senderMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allMessages]);

  const loadMessages = React.useCallback(
    async (orderOverride?: "asc" | "desc") => {
    if (!meetingId) return;

    setLoading(true);
    setError(null);
    setNoChat(false);

    const currentSearch = searchQuery.trim();
    const currentSenderIds = selectedSenders.map((s) => s.id).join(",");

    try {
      // Build params object, only including non-empty values
      const params: any = {
        meetingId,
        limit: PAGE_SIZE,
        sortOrder: orderOverride ?? sortOrder,
      };

      if (currentSearch) {
        params.search = currentSearch;
      }

      if (currentSenderIds) {
        params.senderIds = currentSenderIds;
      }

      const response = await ChatResource.getMeetingChat(params);

      setMessages(response.data || []);
      setTotalMessages(response.paging?.count || 0);
      setNextToken(response.paging?.nextToken || null);
      setHasMore(!!response.paging?.nextToken);
    } catch (err) {
      setMessages([]);
      setTotalMessages(0);
      if (isNoChatError(err)) {
        setNoChat(true);
        return;
      }
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch messages";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  },
    [meetingId, searchQuery, selectedSenders, sortOrder]
  );

  const loadMoreInternal = React.useCallback(
    async (tokenToUse: string | null, retryCount = 0): Promise<void> => {
      const MAX_RETRIES = 10;

      if (!meetingId || !tokenToUse) return;
      if (retryCount >= MAX_RETRIES) return;

      try {
        const currentSearch = searchQuery.trim();
        const currentSenderIds = selectedSenders.map((s) => s.id).join(",");

        const params: any = {
          meetingId,
          limit: PAGE_SIZE,
          nextToken: tokenToUse,
          sortOrder,
        };

        if (currentSearch) {
          params.search = currentSearch;
        }

        if (currentSenderIds) {
          params.senderIds = currentSenderIds;
        }

        const response = await ChatResource.getMeetingChat(params);

        const newMessages = response.data || [];
        const newNextToken = response.paging?.nextToken || null;
        const newHasMore = !!response.paging?.nextToken;

        setMessages((prev) => [...prev, ...newMessages]);
        setNextToken(newNextToken);
        setHasMore(newHasMore);

        // If we got 0 messages but there's more to load (due to filtering),
        // automatically try loading the next page
        if (newMessages.length === 0 && newNextToken) {
          await loadMoreInternal(newNextToken, retryCount + 1);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load more messages";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    },
    [meetingId, searchQuery, selectedSenders, sortOrder]
  );

  const loadMore = React.useCallback(async () => {
    if (!meetingId || !nextToken || loadingMore) return;

    setLoadingMore(true);
    try {
      await loadMoreInternal(nextToken, 0);
    } finally {
      setLoadingMore(false);
    }
  }, [meetingId, nextToken, loadingMore, loadMoreInternal]);

  React.useEffect(() => {
    if (open && meetingId) {
      // Load messages without filters when dialog opens
      setSearchQuery("");
      setSelectedSenders([]);
      setSortOrder("asc");
      setLoading(true);
      setError(null);
      setNoChat(false);

      // First, fetch ALL messages to populate sender dropdown
      ChatResource.getMeetingChat({
        meetingId,
        limit: 100, // Load all for sender list
      })
        .then((allResponse) => {
          setAllMessages(allResponse.data || []);

          // Then fetch first page with pagination
          return ChatResource.getMeetingChat({
            meetingId,
            limit: PAGE_SIZE,
            sortOrder: "asc",
          });
        })
        .then((response) => {
          setMessages(response.data || []);
          setTotalMessages(response.paging?.count || 0);
          setNextToken(response.paging?.nextToken || null);
          setHasMore(!!response.paging?.nextToken);
        })
        .catch((err) => {
          setMessages([]);
          setAllMessages([]);
          setTotalMessages(0);
          if (isNoChatError(err)) {
            setNoChat(true);
            return;
          }
          const errorMessage =
            err instanceof Error ? err.message : "Failed to fetch messages";
          setError(errorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, meetingId]);

  const openDeleteConfirmation = (message: ChatMessage) => {
    const preview =
      message.content.length > 100
        ? message.content.substring(0, 100) + "..."
        : message.content;
    setDeleteConfirmation({
      open: true,
      messageId: message.id,
      messagePreview: preview,
      senderName: message.senderName,
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      open: false,
      messageId: null,
      messagePreview: "",
      senderName: "",
    });
  };

  const handleConfirmDelete = async () => {
    if (!meetingId || !deleteConfirmation.messageId) return;

    const messageId = deleteConfirmation.messageId;
    closeDeleteConfirmation();
    setDeletingMessageId(messageId);

    try {
      await withLoading(
        ChatResource.deleteMessage(meetingId, messageId)
      );

      setSnackbar({
        open: true,
        message: "Message deleted successfully",
        severity: "success",
      });

      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      setTotalMessages((prev) => prev - 1);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete message";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClearFilters = React.useCallback(() => {
    setSearchQuery("");
    setSelectedSenders([]);

    if (meetingId) {
      setLoading(true);
      setError(null);
      setNoChat(false);

      ChatResource.getMeetingChat({
        meetingId,
        limit: 100,
      })
        .then((allResponse) => {
          setAllMessages(allResponse.data || []);
          return ChatResource.getMeetingChat({
            meetingId,
            limit: PAGE_SIZE,
            sortOrder,
          });
        })
        .then((response) => {
          setMessages(response.data || []);
          setTotalMessages(response.paging?.count || 0);
          setNextToken(response.paging?.nextToken || null);
          setHasMore(!!response.paging?.nextToken);
        })
        .catch((err) => {
          setMessages([]);
          setAllMessages([]);
          setTotalMessages(0);
          if (isNoChatError(err)) {
            setNoChat(true);
            return;
          }
          const errorMessage =
            err instanceof Error ? err.message : "Failed to fetch messages";
          setError(errorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [meetingId, sortOrder]);

  const handleToggleSortOrder = React.useCallback(() => {
    const next = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(next);
    // Pass the new order explicitly so we don't read stale state.
    loadMessages(next);
  }, [sortOrder, loadMessages]);

  const hasActiveFilters = searchQuery.trim() || selectedSenders.length > 0;

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string): string => {
    const colors = [
      "#7B61FF",
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6">Chat - {meetingTitle}</Typography>
          {totalMessages > 0 && (
            <Typography variant="body2" color="text.secondary">
              {totalMessages} message{totalMessages !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh messages">
          <IconButton
            onClick={() => loadMessages()}
            disabled={loading}
            size="small"
            color="primary"
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        {/* Search and Filter Controls */}
        {!noChat && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                loadMessages();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Autocomplete
            multiple
            size="small"
            options={availableSenders}
            value={selectedSenders}
            onChange={(_, newValue) => setSelectedSenders(newValue)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedSenders.length === 0
                    ? "Filter by participants..."
                    : ""
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            sx={{ flex: 1, minWidth: 250 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => loadMessages()}
            disabled={loading}
          >
            Apply
          </Button>
          <Tooltip
            title={
              sortOrder === "asc"
                ? "Oldest first (click for newest first)"
                : "Newest first (click for oldest first)"
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleSortOrder}
                disabled={loading}
                startIcon={<SwapVertIcon />}
              >
                {sortOrder === "asc" ? "Oldest first" : "Newest first"}
              </Button>
            </span>
          </Tooltip>
          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              disabled={loading}
            >
              Clear
            </Button>
          )}
        </Box>
        )}

        <Box sx={{ minHeight: 400, maxHeight: 500, overflow: "auto" }}>
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Box
              sx={{
                color: "error.main",
                textAlign: "center",
                py: 4,
              }}
            >
              Error: {error}
            </Box>
          )}
          {!loading && noChat && (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                color: "text.secondary",
              }}
            >
              <Typography variant="body1" gutterBottom>
                No chat available for this meeting
              </Typography>
              <Typography variant="body2">
                This meeting hasn&apos;t started a chat yet.
              </Typography>
            </Box>
          )}
          {!loading && !error && !noChat && messages.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              {hasActiveFilters ? (
                <>
                  <Typography variant="body1" gutterBottom>
                    No messages match your filters
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    sx={{ mt: 2 }}
                  >
                    Clear Filters
                  </Button>
                </>
              ) : (
                <Typography variant="body1">
                  No messages in this chat
                </Typography>
              )}
            </Box>
          )}
          {!loading && !error && messages.length > 0 && (
            <List sx={{ width: "100%" }}>
              {messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      <Tooltip title="Delete message">
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => openDeleteConfirmation(message)}
                          disabled={deletingMessageId === message.id}
                          color="error"
                          size="small"
                        >
                          {deletingMessageId === message.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    }
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                      borderRadius: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={message.senderAvatarUrl || undefined}
                        sx={{
                          bgcolor: message.senderAvatarUrl
                            ? undefined
                            : getAvatarColor(message.senderName),
                        }}
                      >
                        {!message.senderAvatarUrl &&
                          getInitials(message.senderName)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            component="span"
                            variant="subtitle2"
                            fontWeight={600}
                          >
                            {message.senderName}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatDateTime(message.timestamp)}
                          </Typography>
                          {message.isEdited && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              (edited)
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{
                            display: "block",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            mt: 0.5,
                          }}
                        >
                          {message.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < messages.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={loadMore}
                    disabled={loadingMore}
                    startIcon={
                      loadingMore ? <CircularProgress size={16} /> : null
                    }
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={closeDeleteConfirmation}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this message?
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "action.hover",
              borderRadius: 1,
              borderLeft: 3,
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {deleteConfirmation.senderName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {deleteConfirmation.messagePreview}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmation}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
